const THEME_FILTERS_HASH = "#theme-filters";

function withThemeFiltersHash(href) {
  try {
    const url = new URL(href, window.location.origin);
    url.hash = THEME_FILTERS_HASH.slice(1);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    const [withoutHash] = String(href).split("#");
    return `${withoutHash}${THEME_FILTERS_HASH}`;
  }
}

function getThemeFiltersScrollElement() {
  const filters = document.getElementById("theme-filters");
  if (!filters) return null;

  const position = window.getComputedStyle(filters).position;
  if (position === "fixed") {
    return (
      filters.closest(".collection-container") ||
      document.querySelector("filterable-section") ||
      filters
    );
  }

  return filters;
}

function getStickyHeaderOffset() {
  const header = document.getElementById("shopify-section-theme-header");
  if (!header) return 0;

  const position = window.getComputedStyle(header).position;
  if (position !== "sticky" && position !== "fixed") return 0;

  return Math.round(header.getBoundingClientRect().height);
}

function scrollToThemeFilters() {
  const target = getThemeFiltersScrollElement();
  if (!target) return false;

  const top = Math.max(
    0,
    target.getBoundingClientRect().top + window.scrollY - getStickyHeaderOffset()
  );

  window.scrollTo({ top, behavior: "auto" });
  return true;
}

function shouldScrollToThemeFilters() {
  return window.location.hash === THEME_FILTERS_HASH;
}

function initThemeFiltersHashScroll() {
  if (!shouldScrollToThemeFilters()) return;

  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  let cancelled = false;
  const cancel = () => {
    cancelled = true;
  };

  window.addEventListener("wheel", cancel, { once: true, passive: true });
  window.addEventListener("touchstart", cancel, { once: true, passive: true });
  window.addEventListener("keydown", (event) => {
    if (
      ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(
        event.key
      )
    ) {
      cancel();
    }
  });

  const run = () => {
    if (cancelled || !shouldScrollToThemeFilters()) return;
    scrollToThemeFilters();
  };

  run();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  }

  window.addEventListener("load", run, { once: true });
  window.addEventListener("pageshow", run);

  let attempts = 0;
  const retry = window.setInterval(() => {
    attempts += 1;
    run();
    if (cancelled || attempts >= 8) {
      window.clearInterval(retry);
    }
  }, 150);
}

window.addEventListener("hashchange", () => {
  if (shouldScrollToThemeFilters()) {
    scrollToThemeFilters();
  }
});

initThemeFiltersHashScroll();

class ThemeFilters extends HTMLElement {
  constructor() {
    super();
    this.openFiltersButtons = document.querySelectorAll(`.open-filters-button`);
    this.closeFiltersButton = this.querySelector(`.btn-close`);
    this.filtersInside = this.querySelector(".theme-filters-inside");
    this.rootUrl = window.location.pathname;
    this.filterFormQuery = "[data-filters-form]";
    this.setFilterListeners();
  }
  addEventListeners() {
    // Add event listeners to all open filters buttons
    this.openFiltersButtons.forEach(button => {
      button.addEventListener("click", (event) => {
        this.openFilters(event);
      });
    });
    
    this.closeFiltersButton.addEventListener("click", (event) => {
      this.closeFilters(event);
    });
  }
  connectedCallback() {
    this.addEventListeners();
  }

  closeFilters() {
    document.body.style.overflow = "";
    this.filtersInside.classList.remove("open");
    setTimeout(() => {
      this.classList.remove("open");
    }, 200);
  }
  openFilters() {
    document.body.style.overflow = "hidden";
    this.classList.add("open");
    setTimeout(() => {
      this.filtersInside.classList.add("open");
    }, 100);
  }
  calculateSectionRenderingUrl(filterForm) {
    let searchParameters;
    const sortBy = this.querySelector(`input[name="sort_by"]:checked`);
    const hiddenInputQ = this.querySelector('input[type="hidden"][name="q"]');
    if (filterForm) {
      const formData = new FormData(filterForm);
      searchParameters = this.removeEmptyParams(new URLSearchParams(formData));
      searchParameters.append("sort_by", sortBy.value);
      if (hiddenInputQ && hiddenInputQ.value) {
        searchParameters.append("q", hiddenInputQ.value);
      }
    } else {
      searchParameters = new URLSearchParams();
      const searchParameterKeys = /* @__PURE__ */ new Set(["sort_by"]);
      const initialUrlSearchParameters = new URLSearchParams(
        window.location.search
      );
      for (const key of initialUrlSearchParameters.keys()) {
        if (searchParameterKeys.has(key)) {
          searchParameters.append(key, initialUrlSearchParameters.get(key));
        }
      }
      if (hiddenInputQ && hiddenInputQ.value) {
        searchParameters.append("q", hiddenInputQ.value);
      }
    }
    const query = searchParameters.toString();
    const href = query ? `${this.rootUrl}?${query}` : this.rootUrl;
    return withThemeFiltersHash(href);
  }
  applyFilters() {
    const filterForm = this.querySelector(this.filterFormQuery);
    const href = this.calculateSectionRenderingUrl(filterForm);
    this.onFilterFormSubmit(href);
  }
  removeEmptyParams(parameters) {
    const keysForDel = [];
    for (const [key, value] of parameters.entries()) {
      if (value == "") {
        keysForDel.push(key);
      }
    }
    for (const key of keysForDel) {
      parameters.delete(key);
    }
    return parameters;
  }
  setFilterListeners() {
    const filterForm = this.querySelector(this.filterFormQuery);
    const filterInputs = filterForm.querySelectorAll("input");
    for (const input of filterInputs) {
      input.addEventListener("change", () => {
        const href = this.calculateSectionRenderingUrl(filterForm);
        this.onFilterFormSubmit(href);
      });
    }
    const filterResetButtons = this.querySelectorAll("[data-filters-reset]");
    if (filterResetButtons.length === 0) return;
    for (const button of filterResetButtons) {
      button.addEventListener("click", () => {
        const resetHref = this.calculateSectionRenderingUrl();
        this.onFilterFormSubmit(resetHref);
      });
    }
    const filterRemoveButtons = this.querySelectorAll(
      "[data-remove-filter-group]"
    );
    if (filterRemoveButtons.length === 0) return;
    for (const button of filterRemoveButtons) {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.loadSectionFromUrl(button.href, {
          replaceState: true,
        });
      });
    }
  }
  async onFilterFormSubmit(href) {
    location.href = withThemeFiltersHash(href);
  }
}

customElements.define("theme-filters", ThemeFilters);

class RangeInput extends HTMLElement {
  constructor() {
    super();

    this.initVariables();
    this.setEventListeners();
    this.setInitialRange();
  }

  initVariables() {
    this.range = this.querySelector(".range-selected");
    this.rangeInput = this.querySelectorAll(".range-input input");
    this.rangeMin = Number.parseInt(this.dataset.rangeMin);

    this.minValueLabel = this.querySelector("[data-min-value-label]");
    this.maxValueLabel = this.querySelector("[data-max-value-label]");
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    var value = "";
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || "${{amount}}";

    function formatWithDelimiters(number) {
      var precision =
        arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;
      var thousands =
        arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ",";
      var decimal =
        arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : ".";

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split(".");
      var dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands
      );
      var centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  setEventListeners() {
    for (const input of this.rangeInput) {
      input.addEventListener("input", (event) => {
        let minRange = Number.parseInt(this.rangeInput[0].value);
        let maxRange = Number.parseInt(this.rangeInput[1].value);

        if (maxRange - minRange < this.rangeMin) {
          if (event.target.className === "min") {
            this.rangeInput[0].value = maxRange - this.rangeMin;
          } else {
            this.rangeInput[1].value = minRange + this.rangeMin;
          }
        }

        this.range.style.left = (minRange / this.rangeInput[0].max) * 100 + "%";
        this.range.style.right =
          100 - (maxRange / this.rangeInput[1].max) * 100 + "%";

        this.minValueLabel.innerHTML = this.formatMoney(minRange * 100);
        this.maxValueLabel.innerHTML = this.formatMoney(maxRange * 100);
      });
    }
  }

  setInitialRange() {
    let minRange = Number.parseInt(this.rangeInput[0].value);
    let maxRange = Number.parseInt(this.rangeInput[1].value);

    this.range.style.left = (minRange / this.rangeInput[0].max) * 100 + "%";
    this.range.style.right =
      100 - (maxRange / this.rangeInput[1].max) * 100 + "%";
  }
}

customElements.get("range-input") ||
  customElements.define("range-input", RangeInput);

class SortHandler {
  constructor(sortSelector = "#SortBy") {
    this.sortSelector = sortSelector;
    this.init();
  }

  init() {
    $(this.sortSelector).on("change", () => {
      this.updateURLParams();
    });
  }

  updateURLParams() {
    const selectedSort = $(this.sortSelector).val();
    const params = new URLSearchParams(window.location.search);

    if (selectedSort) {
      params.set("sort_by", selectedSort);
    } else {
      params.delete("sort_by");
    }

    const url = new URL(window.location.href);
    url.search = params.toString();
    window.location.assign(withThemeFiltersHash(`${url.pathname}${url.search}`));
  }
}

new SortHandler();
